import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, 
  Send,
  MessageSquare,
  Users,
  Newspaper,
  Handshake
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  { id: 'general', name: 'General Inquiry', icon: MessageSquare },
  { id: 'membership', name: 'Membership Support', icon: Users },
  { id: 'media', name: 'Media Inquiry', icon: Newspaper },
  { id: 'partnership', name: 'Partnership Inquiry', icon: Handshake },
];

const contactInfo = [
  {
    icon: Mail,
    title: 'Email Us',
    value: 'hello@americanrea.com',
    link: 'mailto:hello@americanrea.com'
  }
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiry_type: 'general'
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Message sent successfully! We\'ll be in touch soon.');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          inquiry_type: 'general'
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <span className="text-[#D42B3C] font-semibold text-sm tracking-wider uppercase mb-4 block">
            Contact Us
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Get in Touch
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have a question or need assistance? We're here to help. 
            Reach out and our team will respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                  Contact Information
                </h2>
              </div>

              {contactInfo.map((info) => (
                <a 
                  key={info.title}
                  href={info.link}
                  className="flex items-start gap-4 group"
                  target={info.title === 'Visit Us' ? '_blank' : undefined}
                  rel={info.title === 'Visit Us' ? 'noopener noreferrer' : undefined}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#3D4F7A]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#3D4F7A] transition-colors">
                    <info.icon className="w-6 h-6 text-[#3D4F7A] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                    <p className="text-slate-600 whitespace-pre-line text-sm">{info.value}</p>
                  </div>
                </a>
              ))}

              {/* Inquiry Types */}
              <div className="pt-8 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4">Departments</h3>
                <div className="space-y-3">
                  {inquiryTypes.map((type) => (
                    <div key={type.id} className="flex items-center gap-3 text-sm text-slate-600">
                      <type.icon className="w-4 h-4 text-[#3D4F7A]" />
                      {type.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border border-slate-200 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          required
                          className="h-12"
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="h-12"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="inquiry_type">Inquiry Type</Label>
                        <Select 
                          value={formData.inquiry_type} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, inquiry_type: value }))}
                        >
                          <SelectTrigger className="h-12" data-testid="contact-inquiry-type">
                            <SelectValue placeholder="Select inquiry type" />
                          </SelectTrigger>
                          <SelectContent>
                            {inquiryTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help?"
                          required
                          className="h-12"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more about your inquiry..."
                        required
                        className="min-h-[150px] resize-none"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#3D4F7A] hover:bg-[#2A3755] text-white h-12"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
